import { memo, useCallback, useMemo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { useNavigate } from 'react-router-dom';
import { Tooltip, Spinner, mergeClasses } from '@fluentui/react-components';
import { OrganizationHorizontalRegular, ChevronRightRegular } from '@fluentui/react-icons';
import { ConnectorIcon } from '@/components/ConnectionsView/ConnectorIcon';
import { useWorkflowConnectors, actionNameKey } from '@/hooks/useWorkflowConnectors';
import { useSubWorkflowMetadata } from '@/hooks/useSubWorkflowMetadata';
import { useNodeConnectionStatus } from '@/hooks/useNodeConnectionStatus';
import { ExecutionOverlay } from '@/components/shared';
import {
  FloatingNode,
  useDetailLevel,
  type NodeStatus,
} from '../FloatingNode';
import type { FloatingToolNodeData } from '../FloatingToolNode';
import { getBuiltInManifest } from '@/data/manifests';
import { SubWorkflowPreview } from './SubWorkflowPreview';
import { useStyles } from './SubWorkflowToolNode.styles';

interface SubWorkflowToolNodeProps {
  data: FloatingToolNodeData;
  selected?: boolean;
  id?: string;
}

/**
 * Sub-workflow tool node with visual differentiation.
 * Double-outline icon box, step count badge, chevron prefix for expand/collapse.
 * Node click → edit action; chevron click → expand/collapse inline scope preview.
 */
export const SubWorkflowToolNode = memo(
  ({ data, selected, id }: SubWorkflowToolNodeProps) => {
    const styles = useStyles();
    const detailLevel = useDetailLevel();
    const navigate = useNavigate();

    const { getConnectorInfo, isLoading: isConnectorLoading } = useWorkflowConnectors();
    // See FloatingToolNode for the connection-vs-action lookup rationale.
    const connectorInfo =
      (data.connectionName && getConnectorInfo(data.connectionName)) ||
      (data.actionName ? getConnectorInfo(actionNameKey(data.actionName)) : undefined);

    // Get connection validation status (includes mock detection)
    const { validationResult } = useNodeConnectionStatus({
      nodeId: id ?? data.toolNodeId ?? data.toolName,
      operationType: data.actionType as string,
      connectionReferenceKey: data.connectionName,
    });

    // Fall back to the invoke-workflow built-in manifest so the icon + green
    // brand color match the dock toolbar / picker / canvas action node when
    // no connector lookup hits (which is the common case for sub-workflows).
    const builtInManifest = useMemo(
      () => (connectorInfo ? null : getBuiltInManifest('Workflow')),
      [connectorInfo],
    );

    const iconUri =
      connectorInfo?.iconUri ??
      (builtInManifest?.properties?.iconUri as string | undefined);
    const brandColor =
      connectorInfo?.brandColor ??
      (builtInManifest?.properties?.brandColor as string | undefined);
    const hasIcon = Boolean(iconUri);
    const isManifestLoading = isConnectorLoading && !connectorInfo;

    const isMonitoringMode = data.executionStatus !== undefined || data.wasNotInvoked;
    const isSkipped = data.wasNotInvoked;

    const toolLabel = data.label || data.toolName;
    const subWorkflowName = data.toolName;
    const nodeId = data.toolNodeId ?? data.toolName;

    // Fetch sub-workflow metadata for step count badge
    const { stepCount } = useSubWorkflowMetadata(subWorkflowName);

    const onSubWorkflowExpandToggle = data.onSubWorkflowExpandToggle;
    const isSubWorkflowExpanded = data.isSubWorkflowExpanded;

    const handleExpandClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent node click (edit) from firing
      onSubWorkflowExpandToggle?.(nodeId, !isSubWorkflowExpanded);
    }, [onSubWorkflowExpandToggle, nodeId, isSubWorkflowExpanded]);

    const handleClose = useCallback(() => {
      onSubWorkflowExpandToggle?.(nodeId, false);
    }, [onSubWorkflowExpandToggle, nodeId]);

    const handleEditClick = useCallback(() => {
      if (subWorkflowName) {
        navigate(`../${encodeURIComponent(subWorkflowName)}`, { relative: 'path' });
      }
    }, [navigate, subWorkflowName]);

    const getNodeStatus = (): NodeStatus => {
      if (isSkipped) return 'default';
      if (!data.executionStatus) {
        // Check validation status when not in monitoring mode
        if (validationResult) {
          if (validationResult.status === 'mock-active') return 'mock';
          if (!validationResult.isValid) {
            return validationResult.status === 'warning' ? 'warning' : 'error';
          }
        }
        // Fall back to connection status for backwards compatibility
        if (data.connectionStatus === 'disconnected') return 'error';
        if (data.connectionStatus === 'warning') return 'warning';
        return 'default';
      }
      switch (data.executionStatus) {
        case 'running': return 'running';
        case 'succeeded': return 'success';
        case 'failed': return 'failed';
        case 'skipped': return 'skipped';
        default: return 'default';
      }
    };

    const statusMessage =
      validationResult?.status === 'mock-active'
        ? 'Using test data'
        : validationResult && !validationResult.isValid
          ? validationResult.error?.message ?? 'Connection not configured'
          : data.connectionStatus === 'disconnected'
            ? 'Connection required'
            : data.connectionStatus === 'warning'
              ? 'Connection warning'
              : undefined;

    const renderIcon = () => {
      if (isManifestLoading) {
        return <Spinner size="extra-tiny" aria-label="Loading connector info" />;
      }
      if (hasIcon) {
        return <ConnectorIcon iconUri={iconUri!} size={20} />;
      }
      return <OrganizationHorizontalRegular />;
    };

    const tooltipContent = `${toolLabel}${stepCount > 0 ? ` — ${stepCount} steps` : ''}`;
    const showDetails = detailLevel === 'compact' || detailLevel === 'detailed';

    // Chevron prefix for the label — clickable to expand/collapse
    const chevronPrefix = showDetails ? (
      <span
        className={styles.chevronPrefix}
        role="button"
        tabIndex={0}
        aria-label={`${isSubWorkflowExpanded ? 'Collapse' : 'Expand'} sub-workflow preview`}
        aria-expanded={isSubWorkflowExpanded ?? false}
        onClick={handleExpandClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); handleExpandClick(e as unknown as React.MouseEvent); } }}
      >
        <ChevronRightRegular
          fontSize={12}
          className={mergeClasses(
            styles.chevronIcon,
            isSubWorkflowExpanded && styles.chevronIconExpanded
          )}
        />
      </span>
    ) : undefined;

    return (
      <Tooltip content={tooltipContent} relationship="description" positioning="above">
        <div className={isSkipped ? styles.skippedWrapper : styles.wrapper}>
          <FloatingNode
            icon={renderIcon()}
            label={toolLabel}
            labelPrefix={chevronPrefix}
            detailLevel={detailLevel}
            selected={selected ?? false}
            status={getNodeStatus()}
            statusMessage={statusMessage}
            accentColor="green"
            {...(brandColor ? { brandColorOverride: brandColor } : {})}
            shape="circle"
            doubleOutline
            ariaLabel={`Sub-workflow: ${toolLabel}${stepCount > 0 ? `, ${stepCount} steps` : ''}`}
          >
            <Handle
              type="target"
              position={Position.Bottom}
              id="input"
              isConnectable={false}
              style={{ opacity: 0 }}
            />
          </FloatingNode>

          {stepCount > 0 && (
            <div className={styles.countBadge} aria-label={`${stepCount} steps`}>
              {stepCount}
            </div>
          )}

          {/* Scope-style inline preview — expands below the tool node */}
          {isSubWorkflowExpanded && subWorkflowName && (
            <SubWorkflowPreview
              workflowName={subWorkflowName}
              onEditClick={handleEditClick}
              onClose={handleClose}
            />
          )}

          {isMonitoringMode && !isSkipped && (
            <ExecutionOverlay
              status={data.executionStatus!}
              duration={data.executionDuration}
              position="top-right"
              size="small"
            />
          )}
        </div>
      </Tooltip>
    );
  }
);

SubWorkflowToolNode.displayName = 'SubWorkflowToolNode';
